import { animate, state, style, transition, trigger } from '@angular/animations';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatSidenavContainer } from '@angular/material/sidenav';
import { Router } from '@angular/router';
import { UnRx } from '@pebula/utils';
import { EnvironmentService, UserSessionService } from '@sg/common';
import { SgMenuGroup } from '@sg/ui-kit/core/src/lib/components/sg-menu/sg-menu.model';
import { Observable, ReplaySubject, Subject } from 'rxjs';
import { debounceTime, map, tap } from 'rxjs/operators';
import { SgNavMenuTrigger } from '../../side-nav-menu-trigger';
import { SgSidenavComponent } from '../../sidenav.component';
import { NavGroup, NavItemType, NavLink, SgNavItemDefControllerContext } from '../interfaces';
import { NAV_ITEM_TYPE } from '../models';
import { SgNavMenuService } from '../nav-menu.service';
import { UserMenuRegistry } from '../user-menu';

function isNavGroup(navItem: NavItemType): navItem is NavGroup {
  return navItem.type === NAV_ITEM_TYPE.Group;
}

function expandGroupsOfActive(items: NavItemType[], router: Router): boolean {
  for (const mi of items || []) {
    if (isNavGroup(mi)) {
      const urlTree = router.createUrlTree([mi.url]);
      if (router.isActive(urlTree, false)) {
        mi.expanded = true;
        expandGroupsOfActive(mi.children, router);
        return true;
      }
    }
  }
  return false;
}

@Component({
  selector: 'sg-nav-menu',
  templateUrl: './nav-menu.container.html',
  styleUrls: ['./nav-menu.container.scss'],
  animations: [
    trigger('slideInOut', [
      state('in', style({ height: '*' })),
      transition(':enter', [style({ height: 0 }), animate('0.3s ease-in-out')]),
      transition(':leave', [animate('0.3s ease-in-out', style({ height: 0 }))]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
@UnRx()
export class SgNavMenuComponent implements OnChanges, AfterViewInit, OnDestroy, SgNavItemDefControllerContext {
  @ViewChild('sidenavContent', { static: false }) sidenavContent: ElementRef;
  @ViewChild('userMenuRef', { static: false }) userMenuRef: any;
  @ViewChild('userMenuTrigger') userMenuTrigger: MatMenuTrigger;

  @Input() showExpander: boolean;
  @Input() showLogo: boolean;
  @Input() showAppName: boolean;
  @Input() logoSrc: string;
  @Input() appName: string;
  @Input() expanderPosition: 'top' | 'bottom' = 'top';
  @Input() showUserMenu: boolean;
  @Input() isFloatingSideBar: boolean;
  @Input() showNotifications: boolean;
  @Input() navBarHomeLink: string;
  @Input() set menuItems(value: Observable<NavItemType[]>) {
    // we get the menu items feed.
    // each async NgModule will broadcast new menu items which will emit a new menuItem collection
    // because we have multiple NgModules within each app we want to avoid running this over and over
    // We use deboune for that. The first debouce is just avoid creating redundant menu items copies.
    // It has low debounce time because we want to display the menu items quickly, it's a tradeoff.
    //
    // The 2nd debounce blocks the operation of finding the active link an openning (expending) all groups in the path (if any)
    // We put a longer debounce time here because it's a bit heavier on the CPU.
    // Finding the expended group is not really CPU intensive but this recursive operatoin, running on every event will cause gitter because
    // it happens on first load, when the app init's and the page loads... if we hit a managed Iframe page it will be heavy on the CPU and gitter will appear.
    value
      .pipe(
        debounceTime(50),
        map((items) => items.map((mi) => Object.create(mi))),
        tap((items) => this.onMenuItems(items)),
        debounceTime(250),
        tap((items) => {
          this.tryExpandGroupFirstTime();
          let topMenuItems: NavItemType[] = [];
          let bottomMenuItems: NavItemType[] = [];
          (items as NavItemType[]).forEach((item) => {
            if (item.pinToBottom) {
              bottomMenuItems.push(item);
            } else {
              topMenuItems.push(item);
            }
          });
          this.topMenuItems$.next(topMenuItems);
          this.activeMenuItem.next(topMenuItems[0]);
          this.navMenu.activeMenuItem$.next(topMenuItems[0]);
          this.bottomMenuItems$.next(bottomMenuItems);
          this.menuItemsAdded = true;
        }, UnRx(this))
      )
      .subscribe();
  }

  menuItemsAdded = false;
  isExpanded = false;
  isExpandAnimationComplete = false;
  isBorrower = false;
  activeMenuItem = new ReplaySubject<NavItemType>(1);
  topMenuItems$ = new Subject<NavItemType[]>();
  bottomMenuItems$ = new Subject<NavItemType[]>();
  ctrlContext: SgNavItemDefControllerContext;
  _menuItems: NavItemType[];
  firstName = '';
  lastName = '';
  accountName = '';
  notificationsNum = 0;
  activeRoute = '';
  userMenuItems: any[] = [];
  private userMenuResizeHandler: (() => void) | null = null;
  private runningAnimations = 0;
  private scrollbarObserver: ResizeObserver | null = null;
  private navListElement: HTMLElement;

  constructor(
    public userSession: UserSessionService,
    public navMenu: SgNavMenuService,
    public env: EnvironmentService,
    private _sgSidenav: SgSidenavComponent,
    private _cdr: ChangeDetectorRef,
    private _router: Router,
    private _navMenuView: ElementRef,
    private _userMenuRegistry: UserMenuRegistry
  ) {
    this.ctrlContext = Object.create(this);
    this.menuItems = navMenu.menuTree$;
    this.isExpanded = this._sgSidenav.isExpanded;
    this.isBorrower = this.userSession.env.appName === 'sg-borrower' ? true : false;

    _sgSidenav.expandChange.pipe(UnRx(this)).subscribe((isExpanded) => {
      this.isExpanded = isExpanded;
      this.isExpandAnimationComplete = false;
      if (isExpanded) {
        setTimeout(() => {
          this.isExpandAnimationComplete = true;
          this._cdr.markForCheck();
        }, 500);
      }

      if (!this.isExpanded && this._menuItems) {
        this.collapseAll(this._menuItems);
      } else if (this.isExpanded) {
        this.tryExpandGroupFirstTime();
      }
      this.ctrlContext = Object.create(this);
      this._cdr.markForCheck();
    });

    this.userSession.userProfile$.pipe(UnRx(this)).subscribe((userProfile) => {
      this.firstName = userProfile?.givenName || '';
      this.lastName = userProfile?.familyName || '';
      this.accountName = userProfile?.accountName || '';
      this._cdr.markForCheck();
    });

    this.navMenu.totalNotificationNum$.pipe(UnRx(this)).subscribe((num) => {
      this.notificationsNum = num;
      this._cdr.markForCheck();
    });

    this._router.events.pipe(UnRx(this)).subscribe(() => {
      this.activeRoute = this._router.url; // Includes query params
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('showExpander' in changes) {
      this.showExpander = coerceBooleanProperty(changes.showExpander.currentValue);
    }
  }

  ngAfterViewInit(): void {
    this.setupScrollbarDetection();
  }

  ngOnDestroy(): void {
    if (this.scrollbarObserver) {
      this.scrollbarObserver.disconnect();
      this.scrollbarObserver = null;
    }
  }

  toggleExpandClick(): void {
    this._sgSidenav.toggleExpended();
  }

  isNavGroup(navItem: NavItemType): navItem is NavGroup {
    return isNavGroup(navItem);
  }

  updateGroup(mi: NavGroup, navMenuTrigger: SgNavMenuTrigger): void {
    mi.expanded = !mi.expanded;
    if (mi.expanded && !this.isExpanded) {
      navMenuTrigger.toggleMenu();
    }
  }

  onActivate(mi: NavItemType): void {
    if (!this.isExpanded) {
      mi.expanded = !mi.expanded;
    }
    this.activeMenuItem.next(mi);
    this.navMenu.activeMenuItem$.next(mi);
  }

  menuItemAnimationStart(): void {
    this.runningAnimations += 1;
    if (this.runningAnimations === 1) {
      this.runSidenavAnimation(this._sgSidenav.sidenavContainer);
    }
  }

  menuItemAnimationEnd(): void {
    this.runningAnimations -= 1;
  }

  onNotificationsClick(event?: Event): void {
    this.navMenu.notificationClick$.next(event || null);
  }

  getUserMenuGroups(): SgMenuGroup[] {
    const userMenuService = this._userMenuRegistry?.getActiveService();
    return userMenuService?.getUserMenuGroups() ?? [];
  }

  getUserSubtitle(): string {
    const userMenuService = this._userMenuRegistry?.getActiveService();
    return userMenuService?.getUserSubtitle?.() ?? '';
  }

  onUserMenuOpened(trigger: MatMenuTrigger) {
    const applyPosition = () => {
      const overlayRef = (trigger as any)._overlayRef;
      if (overlayRef) {
        const left = this.isExpanded ? 251 : 91;
        const triggerElement = (trigger as any)._element?.nativeElement as HTMLElement;
        if (triggerElement) {
          const navbarRect = this.navListElement.getBoundingClientRect();
          const overlayEl = overlayRef.overlayElement as HTMLElement;
          overlayEl.style.transform = 'none';
          overlayEl.style.position = 'fixed';
          overlayEl.style.left = `${left}px`;
          overlayEl.style.top = `${navbarRect.bottom - overlayEl.offsetHeight}px`;
          overlayEl.style.right = 'auto';
          overlayEl.style.zIndex = '1000';
          if (overlayEl.children?.length > 0 && overlayEl.children[0]) {
            (overlayEl.children[0] as HTMLElement).style.borderRadius = '8px';
          }
        }
      }
    };

    window.removeEventListener('resize', this.userMenuResizeHandler);
    this.userMenuResizeHandler = () => trigger.closeMenu();
    window.addEventListener('resize', this.userMenuResizeHandler);

    setTimeout(applyPosition);
  }

  onUserMenuClosed() {
    window.removeEventListener('resize', this.userMenuResizeHandler);
  }

  private onMenuItems(items: NavItemType[]): void {
    this._menuItems = items;
  }

  private runSidenavAnimation(matSidenavContainer: MatSidenavContainer): void {
    const self = this;
    const a = function () {
      matSidenavContainer.ngDoCheck();
      if (self.runningAnimations > 0) {
        window.requestAnimationFrame(a);
      }
    };
    a();
  }

  private collapseAll(items: NavItemType[]): void {
    for (const mi of items) {
      if (mi.expanded) {
        mi.expanded = false;
        if ((<NavGroup>mi).children) {
          this.collapseAll((<NavGroup>mi).children as any);
        }
      }
    }
  }

  private tryExpandGroupFirstTime(): void {
    if (this.isExpanded) {
      expandGroupsOfActive(this._menuItems, this._router);
    }
  }

  private setupScrollbarDetection(): void {
    this.navListElement = this._navMenuView.nativeElement.querySelector('mat-nav-list');

    if (!this.navListElement) {
      return;
    }

    const checkScrollbar = () => {
      // Check if content overflows
      const hasOverflow = this.navListElement.scrollHeight > this.navListElement.clientHeight;
      const scrollbarWidth = this.navListElement.offsetWidth - this.navListElement.clientWidth;
      const hasVisibleScrollbar = hasOverflow && scrollbarWidth > 0;

      if (hasVisibleScrollbar) {
        this.navListElement.classList.add('has-scrollbar');
      } else {
        this.navListElement.classList.remove('has-scrollbar');
      }

      // Also add class for overflow regardless of scrollbar visibility
      if (hasOverflow) {
        this.navListElement.classList.add('has-overflow');
      } else {
        this.navListElement.classList.remove('has-overflow');
      }
    };

    this.scrollbarObserver = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        checkScrollbar();
      });
    });

    this.scrollbarObserver.observe(this.navListElement);

    this._sgSidenav.expandChange.pipe(UnRx(this)).subscribe(() => {
      setTimeout(() => {
        checkScrollbar();
      }, 600); // Wait for animation to complete
    });

    // Initial check
    setTimeout(() => {
      checkScrollbar();
    }, 100);
  }
}
